import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { TaskStatusBadge } from "../components/PriorityBadge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Check, ArrowRightCircle } from "lucide-react";

const fmt = (iso, tz) => {
  try { return new Date(iso).toLocaleDateString("en-US", { timeZone: tz || "America/Chicago", month: "short", day: "numeric" }); }
  catch { return iso; }
};

export default function TaskHistory() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/tasks").then((r) => setTasks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const action = async (id, act) => {
    await api.patch(`/tasks/${id}/${act}`);
    toast.success(act === "complete" ? "Task completed" : "Task carried forward");
    load();
  };

  const filtered = tasks.filter((t) => filter === "all" || t.status === filter);

  return (
    <div className="animate-fade-up">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Tracking</p>
      <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Task History</h1>
      <p className="mt-1 text-sm text-slate-500">Every task carried between shifts.</p>

      <Tabs value={filter} onValueChange={setFilter} className="mt-6">
        <TabsList data-testid="task-filter-tabs">
          <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
          <TabsTrigger value="open" data-testid="filter-open">Open</TabsTrigger>
          <TabsTrigger value="completed" data-testid="filter-completed">Completed</TabsTrigger>
          <TabsTrigger value="carried_forward" data-testid="filter-carried">Carried Forward</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="mt-4 rounded-md border border-slate-200 bg-white shadow-sm">
        <Table data-testid="tasks-table">
          <TableHeader>
            <TableRow>
              <TableHead>Task</TableHead>
              <TableHead>Handoff</TableHead>
              <TableHead>Shift</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-slate-400">No tasks found.</TableCell></TableRow>
            ) : filtered.map((t) => (
              <TableRow key={t.id} data-testid={`task-row-${t.id}`}>
                <TableCell className="font-medium text-slate-800">
                  <Link to={`/handoffs/${t.handoff_id}`} className="hover:text-blue-600 transition-colors">{t.title}</Link>
                </TableCell>
                <TableCell className="text-slate-600">
                  <Link to={`/handoffs/${t.handoff_id}`} className="hover:text-blue-600 transition-colors">{t.handoff_title}</Link>
                </TableCell>
                <TableCell className="text-slate-500">{t.handoff_shift}</TableCell>
                <TableCell><TaskStatusBadge status={t.status} /></TableCell>
                <TableCell className="text-slate-500">{fmt(t.created_at, user?.org_timezone)}</TableCell>
                <TableCell className="text-right">
                  {t.status === "open" && (
                    <div className="flex justify-end gap-2">
                      <Button data-testid={`th-complete-${t.id}`} onClick={() => action(t.id, "complete")} size="sm" variant="outline" className="h-8 gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                        <Check className="h-3.5 w-3.5" /> Complete
                      </Button>
                      <Button data-testid={`th-carry-${t.id}`} onClick={() => action(t.id, "carry-forward")} size="sm" variant="outline" className="h-8 gap-1 text-amber-700 border-amber-200 hover:bg-amber-50">
                        <ArrowRightCircle className="h-3.5 w-3.5" /> Carry
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
